import { AfterViewInit, Component, DestroyRef, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';

/** Angular component that utilizes the PrimeNG p-menubar component
 * to display the navigation menu for the application
 */
@Component({
	selector: 'app-nav',
	templateUrl: './nav.component.html',
	standalone: false
})
export class NavComponent implements OnInit, AfterViewInit, OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	private readonly mobileMenuBreakpoint = 912;
	private viewportSyncTimeoutId: number | null = null;

	navMenuItems: MenuItem[] = [];
	sidebarVisible = false;
	isMobileView = false;
	desktopMenuOpen = false;
	desktopBackdropTop = 0;
	allPermissions: string[] = [];
	isScreenTest = false;
	isScreenSales = false;
	isScreenContractReview = false;
	isScreenContractExport = false;
	isScreenContractEdit = false;
	isScreenManualContract = false;
	isScreenSignature = false;
	isScreenPlansPolicy = false;
	isScreenContractView = false;
	isScreenContractSearch = false;
	isScreenContractSearchDealer = false;
	isScreenContractViewDealer = false;
	isScreenVehicleModel = false;
	isScreenSalesTelemarketing = false;
	isScreenSalesFord = false;
	isScreenHome = false;
	isScreenCustomerValidation = false;
	isScreenVinValidation = false;
	isScreenContractImport = false;
	isScreenViewsPlans = false;
	isScreenProcessContractCancel = false;

	baseStyleMenuItem: Record<string, string> = {
		fontSize: 'clamp(1rem, 0.4vw + 0.75rem, 1.2rem)',
		fontWeight: '500',
		width: '100%',
		boxSizing: 'border-box'
	};

	menuAriaObserver: MutationObserver | null = null;
	menuOpenStateObserver: MutationObserver | null = null;

	constructor(
		private apsPermissionService: ApsPermissionService,
		private translateService: TranslateService
	) { }

	@HostListener('window:resize')
	onWindowResize(): void {
		const viewportModeChanged = this.updateViewportState();
		this.updateDesktopBackdropTop();
		if (viewportModeChanged) {
			this.refreshMenuViewportBindings();
			return;
		}

		this.syncDesktopMenuOpenState();
	}

	@HostListener('document:keydown.escape')
	onEscapeKey(): void {
		if (!this.isMobileView && this.desktopMenuOpen) {
			this.closeDesktopMenu();
		}
	}

	ngOnInit() {

		this.updateViewportState();

		setTimeout(() => {

			this.getMenu();
		}, 500);
	}

	ngAfterViewInit(): void {
		this.refreshMenuViewportBindings();
		this.updateDesktopBackdropTop();
	}

	private updateViewportState(): boolean {
		const previousMobileView = this.isMobileView;
		this.isMobileView = window.innerWidth <= this.mobileMenuBreakpoint;
		if (!this.isMobileView) {
			this.sidebarVisible = false;
			return previousMobileView !== this.isMobileView;
		}

		this.desktopMenuOpen = false;
		return previousMobileView !== this.isMobileView;
	}

	private refreshMenuViewportBindings(): void {
		this.disconnectMenuObservers();

		if (this.viewportSyncTimeoutId !== null) {
			window.clearTimeout(this.viewportSyncTimeoutId);
		}

		this.viewportSyncTimeoutId = window.setTimeout(() => {
			this.viewportSyncTimeoutId = null;
			if (!this.isMobileView) {
				this.observeMenuAriaHiddenFocus();
				this.observeDesktopMenuOpenState();
			}

			this.updateDesktopBackdropTop();
			this.syncDesktopMenuOpenState();
		}, 0);
	}

	private disconnectMenuObservers(): void {
		if (this.menuAriaObserver) {
			this.menuAriaObserver.disconnect();
			this.menuAriaObserver = null;
		}

		if (this.menuOpenStateObserver) {
			this.menuOpenStateObserver.disconnect();
			this.menuOpenStateObserver = null;
		}
	}

	showSidebar(): void {
		if (!this.isMobileView) {
			return;
		}
		this.navMenuItems = this.collapseMenuTree(this.navMenuItems);
		this.sidebarVisible = true;
	}

	private collapseMenuTree(items: MenuItem[]): MenuItem[] {
		return items.map((item: MenuItem) => ({
			...item,
			expanded: false,
			items: item.items ? this.collapseMenuTree(item.items) : item.items
		}));
	}

	hideSidebar(): void {
		this.clearFocusedElement();
		this.sidebarVisible = false;
	}

	private onLeafMenuSelect(): void {
		if (this.isMobileView && this.sidebarVisible) {
			this.hideSidebar();
		}
	}

	onSidebarHide(): void {
		this.clearFocusedElement();
	}

	closeDesktopMenu(): void {
		this.clearFocusedElement();
		const menubarElement = document.querySelector('#app-menubar') as HTMLElement | null;

		if (menubarElement) {
			const activeItems = menubarElement.querySelectorAll('.p-menuitem-active, .ui-menuitem-active');
			activeItems.forEach((item: Element) => {
				(item as HTMLElement).classList.remove('p-menuitem-active', 'ui-menuitem-active');
			});
		}

		document.body.dispatchEvent(new MouseEvent('click', { bubbles: true }));
		this.desktopMenuOpen = false;
	}

	private clearFocusedElement(): void {
		const focusedElement = document.activeElement as HTMLElement | null;
		if (focusedElement && typeof focusedElement.blur === 'function') {
			focusedElement.blur();
		}
	}

	private clearFocusedElementInsideMenubar(): void {
		const focusedElement = document.activeElement as HTMLElement | null;
		const menubarElement = document.querySelector('#app-menubar') as HTMLElement | null;

		if (!focusedElement || !menubarElement) {
			return;
		}

		if (menubarElement.contains(focusedElement) && typeof focusedElement.blur === 'function') {
			focusedElement.blur();
		}
	}

	private observeMenuAriaHiddenFocus(): void {
		const menubarElement = document.querySelector('#app-menubar');
		if (!menubarElement || this.menuAriaObserver) {
			return;
		}

		this.menuAriaObserver = new MutationObserver((mutations: MutationRecord[]) => {
			for (const mutation of mutations) {
				if (mutation.type !== 'attributes') {
					continue;
				}

				const target = mutation.target as HTMLElement;
				const isAriaHidden = target?.getAttribute('aria-hidden') === 'true';
				const activeElement = document.activeElement as HTMLElement | null;
				const hasFocusedDescendant = !!activeElement && (target === activeElement || target.contains(activeElement));

				if (isAriaHidden && hasFocusedDescendant) {
					if (activeElement && typeof activeElement.blur === 'function') {
						activeElement.blur();
					}

					if (typeof target.blur === 'function') {
						target.blur();
					}
				}
			}
		});

		this.menuAriaObserver.observe(menubarElement, {
			subtree: true,
			attributes: true,
			attributeFilter: ['aria-hidden']
		});
	}

	private observeDesktopMenuOpenState(): void {
		const menubarElement = document.querySelector('#app-menubar');
		if (!menubarElement || this.menuOpenStateObserver) {
			return;
		}

		this.menuOpenStateObserver = new MutationObserver(() => {
			this.syncDesktopMenuOpenState();
		});

		this.menuOpenStateObserver.observe(menubarElement, {
			subtree: true,
			attributes: true,
			childList: true,
			attributeFilter: ['class', 'style', 'aria-hidden']
		});

		this.syncDesktopMenuOpenState();
	}

	private syncDesktopMenuOpenState(): void {
		const previousDesktopMenuOpen = this.desktopMenuOpen;

		if (this.isMobileView) {
			this.desktopMenuOpen = false;
			this.clearFocusedElementInsideMenubar();
			return;
		}

		const activeSubmenu = document.querySelector(
			'#app-menubar .p-menuitem.p-menuitem-active > .p-submenu-list, #app-menubar .ui-menuitem.ui-menuitem-active > .ui-submenu-list'
		) as HTMLElement | null;

		if (!activeSubmenu) {
			this.desktopMenuOpen = false;
			this.clearFocusedElementInsideMenubar();
			return;
		}

		const ariaHidden = activeSubmenu.getAttribute('aria-hidden');
		const visibleByAria = ariaHidden === null || ariaHidden === 'false';
		const submenuStyle = getComputedStyle(activeSubmenu);
		const visibleByStyle = submenuStyle.display !== 'none';
		const visibleByVisibility = submenuStyle.visibility !== 'hidden';
		const visibleByOpacity = submenuStyle.opacity !== '0';
		const submenuRect = activeSubmenu.getBoundingClientRect();
		const visibleByDimensions = submenuRect.width > 0 && submenuRect.height > 0;

		this.desktopMenuOpen = visibleByAria && visibleByStyle && visibleByVisibility && visibleByOpacity && visibleByDimensions;

		if (this.desktopMenuOpen) {
			this.updateDesktopBackdropTop();
		} else if (previousDesktopMenuOpen) {
			this.clearFocusedElementInsideMenubar();
		}
	}

	private updateDesktopBackdropTop(): void {
		const headerElement = document.querySelector('#app-header') as HTMLElement | null;
		if (!headerElement) {
			this.desktopBackdropTop = 0;
			return;
		}

		const headerRect = headerElement.getBoundingClientRect();
		this.desktopBackdropTop = Math.max(0, Math.round(headerRect.bottom));
	}

	async getListAllPermissions(): Promise<string[]> {
		let allPermissions: string[] = [];
		await this.apsPermissionService.getListPermissions().then((list: string[]) => {
			allPermissions = list;
		});
		return allPermissions
	}

	ngOnDestroy(): void {
		if (this.viewportSyncTimeoutId !== null) {
			window.clearTimeout(this.viewportSyncTimeoutId);
			this.viewportSyncTimeoutId = null;
		}

		this.disconnectMenuObservers();
	}

	async loadScreenPermissions() {
		await this.checkPermission(ApsPermitedScreen.screenSales).then((success: boolean) => {
			if (success === true) {
				this.isScreenSales = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenSalesTelemarketing).then((success: boolean) => {
			if (success === true) {
				this.isScreenSalesTelemarketing = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenContractSearch).then((success: boolean) => {
			if (success === true) {
				this.isScreenContractSearch = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenContractSearchDealer).then((success: boolean) => {
			if (success === true) {
				this.isScreenContractSearchDealer = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenManualContract).then((success: boolean) => {
			if (success === true) {
				this.isScreenManualContract = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenContractImport).then((success: boolean) => {
			if (success === true) {
				this.isScreenContractImport = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenViewsPlans).then((success: boolean) => {
			if (success === true) {
				this.isScreenViewsPlans = true;
			}
		});
		await this.checkPermission(ApsPermitedScreen.screenProcessContractCancel).then((success: boolean) => {
			if (success === true) {
				this.isScreenProcessContractCancel = true;
			}
		});
	}

	async getMenu() {
		/*
		lê todas as permissões do usuário logado 
		e armazena na variável allPermissions
		*/
		await this.getListAllPermissions().then((success: string[]) => {
			this.allPermissions = success;
		});

		await this.loadScreenPermissions();

		this.isScreenSalesTelemarketing = false;
		console.log(`isScreenSalesTelemarketing? ${this.isScreenSalesTelemarketing}`);

		// renderiza os itens de menu, na primeira vez que acessar a tela
		this.navMenuItemsArray();

		// renderiza ao fazer a troca de linguagem
		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(() => {
			void this.navMenuItemsArray();
		});

	}

	async checkPermission(permissionName: string): Promise<boolean> {
		let checkPermission = false;
		this.allPermissions.forEach(pm => {
			if (pm === permissionName) {
				checkPermission = true;
			}
		})

		return checkPermission;
	}

	/*
		async checkPermissionOK(permissionName:string): Promise<boolean> {		
			let permissions = this.apsPermissionService.getListPermissions();
			let checkPermission = false;
	
			await permissions.then((pm: String[]) => {
				pm.forEach(name => {
					if (name === permissionName) {
						checkPermission = true;
					}
				}) 
			})
			return checkPermission;
		}
	*/

	async navMenuItemsArray() {

		this.navMenuItems = [
			{
				label: this.translateService.instant('TRANSLATE.HOME'),
				icon: 'pi pi-fw pi-home',
				routerLink: '/home',
				command: () => this.onLeafMenuSelect()
			},
			{
				label: this.translateService.instant('TRANSLATE.CONTRACT'),
				icon: 'pi pi-fw pi-file',
				items: await this.getItensContrato()
			},
			{
				label: this.translateService.instant('TRANSLATE.PROFILE'),
				icon: 'pi pi-fw pi-user',
				visible: false,
				items: [
					{
						label: this.translateService.instant('TRANSLATE.EDIT'),
						icon: 'pi pi-fw pi-user-edit',
						visible: false,
					},
					{
						label: this.translateService.instant('TRANSLATE.EXIT'),
						icon: 'pi pi-fw pi-power-off',
						visible: false,
					}
				]
			}
		]

		if (this.isScreenViewsPlans) {
			this.navMenuItems = this.navMenuItems.concat(
				{
					label: this.translateService.instant('TRANSLATE.ADMINISTRATION'),
					icon: 'fa fa-users',
					items: await this.getItensBackOffice()
				}
			);
		}

	}

	async getItensContrato(): Promise<MenuItem[]> {
		let novaListaContrato: MenuItem[] = [];

		if (this.isScreenSales) {
			novaListaContrato = novaListaContrato.concat(
				{
					label: this.translateService.instant('TRANSLATE.NEW'),
					icon: 'fas fa-address-card',
					routerLink: '/sales-channel',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		if (this.isScreenSalesTelemarketing) {
			novaListaContrato = novaListaContrato.concat(
				{
					label: this.translateService.instant('TRANSLATE.NEW.EXTERNAL'),
					icon: 'fas fa-address-card',
					routerLink: '/sales-channel-external',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		if (this.isScreenContractSearch) {
			novaListaContrato = novaListaContrato.concat(
				{
					label: this.translateService.instant('TRANSLATE.SEARCH'),
					icon: 'fas fa-search',
					routerLink: '/contract-search',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		if (this.isScreenContractSearchDealer) {
			novaListaContrato = novaListaContrato.concat(
				{
					label: this.translateService.instant('TRANSLATE.SEARCH'),
					icon: 'fas fa-search',
					routerLink: '/contract-search-dealer',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}


		if (this.isScreenManualContract) {
			novaListaContrato = novaListaContrato.concat(
				{
					label: this.translateService.instant('TRANSLATE.MANUAL'),
					icon: 'fas fa-file',
					routerLink: '/contract-manual',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		//Os campos e menu abaixo, devem permacer comentados, para futuramente serem usados novamente		
		// if (this.isScreenContractImport) {
		// 	novaListaContrato = novaListaContrato.concat(
		// 		{
		// 			label: this.translateService.instant('TRANSLATE.IMPORT'), 
		// 			icon: 'pi pi-fw pi-cloud-upload',
		// 			routerLink: '/import-contract',
		// 			command: this.hideSidebar
		// 		}	
		// 	);
		// }


		return novaListaContrato;
	}

	async getItensBackOffice(): Promise<MenuItem[]> {

		let newListBackOffice: MenuItem[] = [];

		if (this.isScreenViewsPlans) {
			newListBackOffice = newListBackOffice.concat(
				{
					label: this.translateService.instant('TRANSLATE.PLAN'),
					icon: 'fa fa-paste',
					routerLink: '/view-plans',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		if (this.isScreenProcessContractCancel) {
			newListBackOffice = newListBackOffice.concat(
				{
					label: this.translateService.instant('TRANSLATE.PENDING.CANCELLATIONS'),
					icon: 'fas fa-file-invoice',
					routerLink: '/view-pending-cancellations',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		if (this.isScreenViewsPlans) {
			newListBackOffice = newListBackOffice.concat(
				{
					label: this.translateService.instant('TRANSLATE.REPORT'),
					icon: 'fas fa-file-excel',
					routerLink: '/reports',
					command: () => this.onLeafMenuSelect(),
					style: this.baseStyleMenuItem
				}
			);
		}

		return newListBackOffice;
	}

}